import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Client } from '@/api/main/client'
import { getDriveStatisticsOptions } from '@/api/navigationApi/@tanstack/react-query.gen'
import { FaThumbsUp, FaThumbsDown, FaFlagCheckered } from 'react-icons/fa'
import { Text, Button, Grid, Box, useMantineTheme } from '@mantine/core'
import AlertContainer from './AlertContainer'
import { useMemo } from 'react'

interface PatrolFinishedAlertProps {
  client: Client | undefined
  onFinish: () => void
  isFinishing: boolean
}

const DriveStatistics = ({ client }: { client: Client | undefined }) => {
  const { t } = useTranslation(['navigation', 'alerts', 'buttons'])
  const { colors } = useMantineTheme()

  const { data, isLoading, error } = useQuery({
    ...getDriveStatisticsOptions({ client }),
  })

  const statistics = useMemo(() => {
    if (!data) return null

    const totalSegments = data.missed_segment_count + data.valid_driven_route_segment_count
    const missedPercentage = totalSegments > 0 ? (data.missed_segment_count / totalSegments) * 100 : 0
    const isMissedPercentageHigh = missedPercentage >= 4
    const missedPercentageColor =
      missedPercentage >= 10 ? colors.red[9] : isMissedPercentageHigh ? colors.orange[7] : colors.indigo[9]

    const skippedTotal = data.skipped_segment_count + data.valid_driven_route_segment_count
    const skippedPercentage = skippedTotal > 0 ? (data.skipped_segment_count / skippedTotal) * 100 : 0
    const skippedPercentageColor =
      skippedPercentage >= 10 ? colors.red[9] : skippedPercentage >= 4 ? colors.orange[7] : colors.indigo[9]

    return {
      missed: {
        count: data.missed_segment_count,
        percentage: missedPercentage,
        isHigh: isMissedPercentageHigh,
        color: missedPercentageColor,
      },
      skipped: {
        count: data.skipped_segment_count,
        percentage: skippedPercentage,
        isHigh: skippedPercentage >= 4,
        color: skippedPercentageColor,
      },
    }
  }, [data, colors])

  if (isLoading) {
    return (
      <Box style={{ height: '10vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Text>{t('loadingStatistics')}</Text>
      </Box>
    )
  }

  if (error || !statistics) {
    return (
      <Text size="lg" c="var(--mantine-color-text)">
        {t('fetchingDriveStatisticsFailed')}
      </Text>
    )
  }

  return (
    <Grid>
      <Grid.Col span={6}>
        <Text size="xl" fw="bold">
          {t('missedSegments')}
        </Text>
        <Text size="xl" fz={50} c={statistics.missed.color}>
          {statistics.missed.isHigh ? (
            <>
              {statistics.missed.count} <FaThumbsDown />
            </>
          ) : (
            <FaThumbsUp />
          )}
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <Text size="xl" fw="bold">
          {t('skippedSegments')}
        </Text>
        <Text size="xl" fz={50} c={statistics.skipped.color}>
          {statistics.skipped.count} {statistics.skipped.isHigh ? <FaThumbsDown /> : <FaThumbsUp />}
        </Text>
      </Grid.Col>
    </Grid>
  )
}

const PatrolFinishedAlert = ({ client, onFinish, isFinishing }: PatrolFinishedAlertProps) => {
  const { t } = useTranslation(['navigation', 'alerts', 'buttons'])

  return (
    <AlertContainer type="message" position="center" opacity="high" height="large">
      <Box>
        <Text size="xl" fw="bold" mb="xl" ta="center">
          {t('scheduledRouteFinishedBody', { ns: 'alerts' })}
        </Text>
        <Box mb="xl">
          <DriveStatistics client={client} />
        </Box>
        <Button onClick={onFinish} leftSection={<FaFlagCheckered />} loading={isFinishing} size="xl" fullWidth>
          {t('finish', { ns: 'buttons' })}
        </Button>
      </Box>
    </AlertContainer>
  )
}

export default PatrolFinishedAlert
